/* eslint-disable max-statements */
import React, { useState, useEffect } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { toast } from 'react-toastify';
import { Button } from 'primereact/button';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetAnalysisTypesQuery, useUpdateSessionReviewMutation } from "../coachingActionPlanSlice";
import { GradientDefs } from '../../../components/metricMonthlySummaryCard/GradientDefs';
import { MetricMonthlySummaryCard } from "../../../components/metricMonthlySummaryCard/MetricMonthlySummaryCard";
import { ExploreSection, FrameSection, LayoutSection, UnderstandSection } from '../coachStep/components';
import { useActionData, useLayoutForm } from '../coachStep/hooks/useCoachStep';
import { OtherActionsSection } from './OtherActionsSection';
import './ReviewAdjust.scss';
import { CoachingTabs } from '../../../utils/typeConstants';
import DescriptionBox from '../../../components/descriptionBox/DescriptionBox';
import { toLocalDateString } from '../../../utils/dateUtils';
import { metricValueFormat } from '../../../utils/metricValueFormat';
import { useCAPAutoSave } from '../hooks/useCAPAutoSave';
import { isTieredScorecard } from '../../../utils/metricAttainment';

interface ReviewAdjustProps {
    CAPSession: any;
    showOtherActions?: boolean;
    showOnlyOtherActions?: boolean;
    latestSession?: any;
}

interface SessionData {
    analysisTypes: any;
    analysisNotes: string;
    frameNotes: string;
    understandNotes: string;
    exploreNotes: string;
    coachingSkillId: number | null;
    coachingBehaviorId: number | null;
    coachingActionId: number | null;
    actionDescription: string;
    successCriteria: string;
    notes: string;
    performanceCommitment: number | null;
    followUpDate: string | null;
}

export const ReviewAdjust: React.FC<ReviewAdjustProps> = ({
    CAPSession,
    showOtherActions = true,
    showOnlyOtherActions = false,
    latestSession: latestSessionProp
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const latestSession = latestSessionProp || CAPSession?.sessions?.reduce((max: any, session: any) =>
        session.sessionNumber > max.sessionNumber ? session : max
        , CAPSession?.sessions?.[0]);

    const sessionId = latestSession?.coachingActionPlanSessionId;

    const selectedMetric = {
        target: latestSession?.metricTargetValue,
        metricAttained: latestSession?.sessionCommitmentAttained ?? false,
        metricName: CAPSession?.label,
        metricTypeName: CAPSession?.metricTypeName,
        currentValue: latestSession?.metricCurrentValue,
        metricDesc: CAPSession?.label,
        metricDirectionId: latestSession?.metricDirectionId,
        metricIndex: CAPSession?.metricIndex ?? 0,
        metricOmitted: false,
        metricResultValueAttainmentRank: latestSession?.metricResultValueAttainmentRank,
        metricResultValueAttainmentLevelLabel: latestSession?.metricResultValueAttainmentLevelLabel,
        min: CAPSession?.min,
        max: CAPSession?.max,
        targets: CAPSession?.scorecardCalculationRuleId === 2 ? latestSession?.targets : latestSession?.metricTargetValue ?? CAPSession?.target,
    };

    const { data: analysisTypesData, isLoading: isLoadingTypes } = useGetAnalysisTypesQuery();
    const [updateSessionReview, { isLoading: isSaving }] = useUpdateSessionReviewMutation();

    const [frameNotes, setFrameNotes] = useState('');
    const [understandNotes, setUnderstandNotes] = useState('');
    const [exploreNotes, setExploreNotes] = useState('');
    const [analysisNotes, setAnalysisNotes] = useState('');
    const [analysisTypes, setAnalysisTypes] = useState<number[]>([]);
    const [showValidation, setShowValidation] = useState(false);

    const layoutForm = useLayoutForm();
    const { coachingActions } = useActionData(layoutForm.behaviorId);

    useEffect(() => {
        if (!latestSession) return;

        setFrameNotes(latestSession.frameNotes ?? '');
        setUnderstandNotes(latestSession.understandNotes ?? '');
        setExploreNotes(latestSession.exploreNotes ?? '');
        setAnalysisNotes(latestSession.analysisNotes ?? '');
        setAnalysisTypes(latestSession.analysisTypes ?? latestSession.analysis_types ?? []);
        layoutForm.setSkillId(latestSession.coachingSkillId ?? null);
        layoutForm.setBehaviorId(latestSession.coachingBehaviorId ?? null);
        layoutForm.setActionId(latestSession.coachingActionId ?? null);
        layoutForm.setActionDescription(latestSession.actionDescription ?? '');
        layoutForm.setSuccessCriteria(latestSession.successCriteria ?? '');
        layoutForm.setNotes(latestSession.notes ?? '');
        let commitmentDisplay = '';
        if (latestSession.performanceCommitment != null) {
            if (CAPSession?.metricTypeName === 'Percentage') {
                commitmentDisplay = (latestSession.performanceCommitment * 100).toFixed(2);
            } else {
                commitmentDisplay = parseFloat(String(latestSession.performanceCommitment)).toFixed(2);
            }
        }
        layoutForm.setPerformanceCommitment(commitmentDisplay);
        const followUpDate = latestSession.followUpDate
            ? new Date(latestSession.followUpDate)
            : null;
        layoutForm.setFollowUpDate(followUpDate);

    }, [CAPSession]);

    const analysisTypeOptions = analysisTypesData?.data?.map((type: any) => ({
        label: type.coaching_action_plan_analysis_type_name,
        value: type.coaching_action_plan_analysis_type_id,
    })) || [];

    const handleSkillChange = (value: number) => {
        layoutForm.setSkillId(value);
        layoutForm.setBehaviorId(null);
        layoutForm.setActionId(null);
    };

    const handleBehaviorChange = (value: number) => {
        layoutForm.setBehaviorId(value);
        layoutForm.setActionId(null);
    };

    const handleSaveAndContinue = async () => {
        setShowValidation(true);

        if (!layoutForm.skillId || !layoutForm.behaviorId || !layoutForm.actionId) {
            toast.warn('Please select Skill, Behavior, and Action before continuing.');
            return;
        }

        if (!sessionId) {
            toast.error('No session ID available.');
            return;
        }
        /* eslint-disable camelcase */
        try {
            await updateSessionReview({
                coaching_action_plan_session_id: sessionId,
                frame_notes: frameNotes,
                understand_notes: understandNotes,
                explore_notes: exploreNotes,
                coaching_skill_id: layoutForm.skillId!,
                coaching_behavior_id: layoutForm.behaviorId!,
                coaching_action_id: layoutForm.actionId!,
                action_description: layoutForm.actionDescription,
                success_criteria: layoutForm.successCriteria,
                notes: layoutForm.notes,
                performance_commitment: CAPSession?.metricTypeName === 'Percentage'
                    ? parseFloat(layoutForm.performanceCommitment) / 100
                    : parseFloat(layoutForm.performanceCommitment),
                analysis_notes: analysisNotes,
                analysis_type_ids: analysisTypes,
                follow_up_date: layoutForm.followUpDate
                    ? toLocalDateString(layoutForm.followUpDate)
                    : null,
            }).unwrap();

            toast.success('Session saved successfully.');
            setShowValidation(false);
        } catch (error) {
            console.error('UpdateSessionReview failed:', error);
            toast.error('Failed to save session. Please try again.');
        }
    };

    const { updateField } = useCAPAutoSave<SessionData>({
        data: latestSession,
        onSave: async (updatedData) => {
            if (!sessionId) {
                // toast.error('No session ID available.');
                return;
            }
            /* eslint-disable camelcase */
            try {
                const getPerformanceCommitment = () => {
                    if (updatedData?.performanceCommitment == null) return null;
                    const value = parseFloat(updatedData.performanceCommitment.toString());
                    return CAPSession?.metricTypeName === 'Percentage' ? value / 100 : value;
                };
                await updateSessionReview({
                    coaching_action_plan_session_id: sessionId,
                    analysis_notes: updatedData?.analysisNotes,
                    analysis_type_ids: updatedData?.analysisTypes,
                    frame_notes: updatedData?.frameNotes,
                    understand_notes: updatedData?.understandNotes,
                    explore_notes: updatedData?.exploreNotes,
                    coaching_skill_id: updatedData?.coachingSkillId ?? null,
                    coaching_behavior_id: updatedData?.coachingBehaviorId ?? null,
                    coaching_action_id: updatedData?.coachingActionId ?? null,
                    action_description: updatedData?.actionDescription,
                    success_criteria: updatedData?.successCriteria,
                    notes: updatedData?.notes,
                    // Divide ÷ 100 to convert user-facing percentage back to decimal fraction for storage
                    performance_commitment: getPerformanceCommitment(),
                    // Use toLocalDateString to avoid UTC offset shifting the date back one day
                    follow_up_date: updatedData?.followUpDate
                        ? toLocalDateString(new Date(updatedData.followUpDate))
                        : null,
                }).unwrap();

                // toast.success('Auto saved successfully.');
                setShowValidation(false);
            } catch (error) {
                console.error('UpdateSessionReview failed:', error);
                toast.error('Failed to save auto save.');
            }
        },
        idleTimeout: 15000,
    });

    const onBeforeOverride = async () => {
        if (!sessionId) {
            return;
        }
        try {
            await updateSessionReview({
                coaching_action_plan_session_id: sessionId,
                frame_notes: frameNotes,
                understand_notes: understandNotes,
                explore_notes: exploreNotes,
                coaching_skill_id: layoutForm.skillId!,
                coaching_behavior_id: layoutForm.behaviorId!,
                coaching_action_id: layoutForm.actionId!,
                action_description: layoutForm.actionDescription,
                success_criteria: layoutForm.successCriteria,
                notes: layoutForm.notes,
                performance_commitment: CAPSession?.metricTypeName === 'Percentage'
                    ? parseFloat(layoutForm.performanceCommitment) / 100
                    : parseFloat(layoutForm.performanceCommitment),
                analysis_notes: analysisNotes,
                analysis_type_ids: analysisTypes,
                follow_up_date: layoutForm.followUpDate
                    ? toLocalDateString(layoutForm.followUpDate)
                    : null,
            });
            toast.success('Draft saved successfully');
        } catch {
            toast.error('Failed to save draft');
        }
    };

    if (!sessionId && !showOnlyOtherActions) {
        return (
            <div className="review-adjust review-adjust--loading">
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                <p>Loading session data...</p>
            </div>
        );
    }

    // If showing only Other Actions, render just that component
    if (showOnlyOtherActions) {
        return (
            <OtherActionsSection
                CAPSession={CAPSession}
                latestSession={latestSession}
                onActionPlanClosed={() => {
                    sessionStorage.removeItem('cap_nav_intent');
                    navigate(location.pathname, {
                        replace: true,
                        state: {
                            activeStep: 0,
                            coachingActionPlanId: null,
                            coachingActionPlanSessionId: null
                        }
                    });
                }}
            />
        );
    }

    return (
        <div className="review-adjust">
            <GradientDefs />

            {/* Row 1: Metric Card + Analysis */}
            <div className="review-adjust__row">
                <div className="review-adjust__metric">
                    <MetricMonthlySummaryCard
                        targetVal={selectedMetric.target}
                        metricAttained={selectedMetric.metricAttained}
                        metricNameVal={selectedMetric.metricName}
                        metricTypeName={selectedMetric.metricTypeName}
                        metricVal={selectedMetric.currentValue}
                        metricDesc={selectedMetric.metricDesc}
                        metricDirectionId={selectedMetric.metricDirectionId}
                        onCardClick={() => { }}
                        selectedMetric={selectedMetric.metricName}
                        index={selectedMetric.metricIndex}
                        metricOmitted={selectedMetric.metricOmitted}
                        via={CoachingTabs.CoachingActionPlan}
                        CAPCurrentSession={latestSession}
                        metricTypeId={CAPSession?.metricTypeId}
                        overrideInfo={
                            latestSession?.metricCurrentValueOverride != null
                                ? {
                                    label: 'Overridden Data',
                                    value: metricValueFormat(
                                        latestSession.metricCurrentValueOverride,
                                        CAPSession.metricTypeName
                                    ),
                                    description: latestSession.metricCurrentValueOverrideReason ?? '',
                                }
                                : undefined
                        }
                        onBeforeOverride={onBeforeOverride}
                        variant={isTieredScorecard(CAPSession?.scorecardCalculationRuleId) ? 'tiered' : 'legacy'}
                        attainmentRank={selectedMetric?.metricResultValueAttainmentRank}
                        attainmentLevelLabel={selectedMetric?.metricResultValueAttainmentLevelLabel}
                        targets={selectedMetric?.targets}
                        min={selectedMetric?.min}
                        max={selectedMetric?.max}
                    />
                </div>

                <div className="review-adjust__analysis">
                    <div className="review-adjust__analysis-type">
                        <label htmlFor="ra-analysis-type">Analysis Type</label>
                        <MultiSelect
                            id="ra-analysis-type"
                            value={analysisTypes}
                            options={analysisTypeOptions}
                            // onChange={(e) => setAnalysisTypes(e.value)}
                            onChange={(e) => {
                                setAnalysisTypes(e.value);
                                updateField('analysisTypes', e.value);
                            }}
                            placeholder="Select"
                            className="review-adjust__multiselect"
                            disabled={isLoadingTypes}
                            selectedItemsLabel={
                                analysisTypes?.length > 0
                                    ? analysisTypeOptions
                                        .filter((opt: { label: string; value: number }) => analysisTypes.includes(opt.value))
                                        .map((opt: { label: string; value: number }) => opt.label)
                                        .join(', ')
                                    : 'Select'
                            }
                            maxSelectedLabels={0}
                        />
                    </div>

                    <div className="review-adjust__notes">
                        <DescriptionBox
                            title={`Analysis for ${(CAPSession?.employeeName ?? '').replace(/\(/, ' (')}`}
                            responseDescription={analysisNotes}
                            value={analysisNotes}
                            limit={4000}
                            onChange={(val: string) => {
                                setAnalysisNotes(val);
                                updateField('analysisNotes', val);
                            }}
                            via={CoachingTabs.CoachingActionPlan}
                        />
                    </div>
                </div>
            </div>

            {/* Row 2: FUEL Accordion */}
            <div className="review-adjust__fuel">
                <Accordion multiple activeIndex={[0]}>
                    <AccordionTab header={<div className="fuel-header"><span className="fuel-icon">F</span><span>Frame</span></div>}>
                        <FrameSection frameNotes={frameNotes}
                            onFrameNotesChange={(val) => {
                                setFrameNotes(val);
                                updateField('frameNotes', val);
                            }} />
                    </AccordionTab>
                    <AccordionTab header={<div className="fuel-header"><span className="fuel-icon">U</span><span>Understand</span></div>}>
                        <UnderstandSection understandNotes={understandNotes} onUnderstandNotesChange={(val) => {
                            setUnderstandNotes(val);
                            updateField('understandNotes', val);
                        }} />
                    </AccordionTab>
                    <AccordionTab header={<div className="fuel-header"><span className="fuel-icon">E</span><span>Explore</span></div>}>
                        <ExploreSection exploreNotes={exploreNotes} onExploreNotesChange={(val) => {
                            setExploreNotes(val);
                            updateField('exploreNotes', val);
                        }} />
                    </AccordionTab>
                    <AccordionTab header={<div className="fuel-header"><span className="fuel-icon">L</span><span>Layout</span></div>}>
                        <LayoutSection
                            metricName={selectedMetric.metricName}
                            metricCurrentValue={selectedMetric.currentValue}
                            metricTypeName={selectedMetric.metricTypeName}
                            performanceCommitment={layoutForm.performanceCommitment}
                            // onPerformanceCommitmentChange={layoutForm.setPerformanceCommitment}
                            onPerformanceCommitmentChange={(val) => {
                                layoutForm.setPerformanceCommitment(val);
                                updateField('performanceCommitment', val);
                            }}
                            followUpDate={layoutForm.followUpDate}
                            // onFollowUpDateChange={layoutForm.setFollowUpDate}
                            onFollowUpDateChange={(val) => {
                                layoutForm.setFollowUpDate(val);
                                updateField('followUpDate', val);
                            }}
                            skillId={layoutForm.skillId}
                            // onSkillChange={handleSkillChange}
                            onSkillChange={(val) => {
                                handleSkillChange(val);
                                updateField('coachingSkillId', val);
                            }}
                            behaviorId={layoutForm.behaviorId}
                            // onBehaviorChange={handleBehaviorChange}
                            onBehaviorChange={(val) => {
                                handleBehaviorChange(val);
                                updateField('coachingBehaviorId', val);
                            }}
                            actionId={layoutForm.actionId}
                            // onActionChange={layoutForm.setActionId}
                            onActionChange={(val) => {
                                layoutForm.setActionId(val);
                                updateField('coachingActionId', val);
                            }}
                            actionDescription={layoutForm.actionDescription}
                            // onActionDescriptionChange={layoutForm.setActionDescription}
                            onActionDescriptionChange={(val) => {
                                layoutForm.setActionDescription(val);
                                updateField('actionDescription', val);
                            }}
                            successCriteria={layoutForm.successCriteria}
                            // onSuccessCriteriaChange={layoutForm.setSuccessCriteria}
                            onSuccessCriteriaChange={(val) => {
                                layoutForm.setSuccessCriteria(val);
                                updateField('successCriteria', val);
                            }}
                            notes={layoutForm.notes}
                            // onNotesChange={layoutForm.setNotes}
                            onNotesChange={(val) => {
                                layoutForm.setNotes(val);
                                updateField('notes', val);
                            }}
                            coachingActions={coachingActions}
                            showValidation={showValidation}
                            CAPCurrentSession={latestSession}
                            onBeforeOverride={onBeforeOverride}
                        />
                    </AccordionTab>
                </Accordion>
            </div>

            {/* Save and Continue Button */}
            <div className="review-adjust__save">
                <Button
                    label="Save and Continue"
                    icon="pi pi-arrow-circle-right"
                    className="review-adjust__save-btn"
                    onClick={handleSaveAndContinue}
                    loading={isSaving}
                    disabled={isSaving}
                />
            </div>

            {/* Other Actions */}
            {showOtherActions && (
                <OtherActionsSection
                    CAPSession={CAPSession}
                    latestSession={latestSession}
                    onActionPlanClosed={() => {
                        sessionStorage.removeItem('cap_nav_intent');
                        navigate(location.pathname, {
                            replace: true,
                            state: {
                                activeStep: 0,
                                coachingActionPlanId: null,
                                coachingActionPlanSessionId: null
                            }
                        });
                    }}
                />
            )}
        </div>
    );
};
